import { Archive, Database, EllipsisVertical, Pencil } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { EditCaseDialog } from "~/features/case/components/EditCaseDialog";
import { cn, enableForEnv, Stages } from "~/lib/utils";
import { trackEvent } from "~/utils/analytics";
import { ArchiveCaseAlertDialog } from "./ArchiveCaseAlertDialog";
import { DeleteCaseAlertDialog } from "./DeleteCaseAlertDialog";
import { SetAsDefaultCaseDialog } from "./SetAsDefaultCaseDialog";

const DEV_DROPDOWN_MENU_MIN_WIDTH_CLASS = "min-w-[220px]";

const DEV_ADMIN_MENU_ITEM_CLASS =
  "!text-amber-700 dark:!text-amber-400 hover:!bg-amber-50 dark:hover:!bg-amber-950/50 hover:!text-amber-900 dark:hover:!text-amber-300 focus:!bg-amber-50 dark:focus:!bg-amber-950/50 focus:!text-amber-900 dark:focus:!text-amber-300";

interface CaseActionsProps {
  caseId: string;
}

export const CaseActions: FC<CaseActionsProps> = ({ caseId }) => {
  const [openEditCaseDialog, setOpenEditCaseDialog] = useState(false);
  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
  const [showArchiveAlertDialog, setShowArchiveAlertDialog] = useState(false);
  const [openSetAsDefaultDialog, setOpenSetAsDefaultDialog] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const isDevStage = enableForEnv([Stages.DEV, Stages.LOCAL]);

  useEffect(() => {
    if (dropdownOpen) {
      trackEvent({ eventName: "app/cases/case_actions.dropdown/open" });
    }
  }, [dropdownOpen]);

  const dropdownItems = [
    {
      icon: Pencil,
      messageId: "edit",
      onClick: () => {
        trackEvent({ eventName: "app/case_actions_menu/edit.link/click" });
        setOpenEditCaseDialog(true);
        setDropdownOpen(false);
      },
    },
    {
      icon: Archive,
      messageId: "archive",
      defaultMessage: "Archive",
      onClick: () => {
        trackEvent({ eventName: "app/case_actions_menu/archive.link/click" });
        setShowArchiveAlertDialog(true);
        setDropdownOpen(false);
      },
    },
    ...(isDevStage
      ? [
          {
            icon: Database,
            label: "DEV only: Add to Demo cases",
            onClick: () => {
              trackEvent({
                eventName: "app/case_actions_menu/set_as_default.link/click",
              });
              setOpenSetAsDefaultDialog(true);
              setDropdownOpen(false);
            },
            menuItemClassName: DEV_ADMIN_MENU_ITEM_CLASS,
          },
        ]
      : []),
  ];

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <EllipsisVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className={isDevStage ? DEV_DROPDOWN_MENU_MIN_WIDTH_CLASS : "w-32"}
          align="end"
        >
          <DropdownMenuGroup>
            {dropdownItems.map((item) => (
              <div key={"messageId" in item ? item.messageId : item.label}>
                <DropdownMenuItem
                  className={cn(
                    "cursor-pointer",
                    "menuItemClassName" in item && item.menuItemClassName
                  )}
                  onClick={item.onClick}
                >
                  <item.icon className="mr-2 h-4 w-4 shrink-0" />
                  {"messageId" in item ? (
                    <FormattedMessage
                      id={item.messageId}
                      defaultMessage={item.defaultMessage}
                    />
                  ) : (
                    <span className="whitespace-normal text-left leading-snug">
                      {item.label}
                    </span>
                  )}
                </DropdownMenuItem>
              </div>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {showArchiveAlertDialog && (
        <ArchiveCaseAlertDialog
          open={showArchiveAlertDialog}
          setOpen={setShowArchiveAlertDialog}
          projectId={caseId}
        />
      )}
      {showDeleteAlertDialog && (
        <DeleteCaseAlertDialog
          open={showDeleteAlertDialog}
          setOpen={setShowDeleteAlertDialog}
          projectId={caseId}
        />
      )}
      {openEditCaseDialog && (
        <EditCaseDialog
          open={openEditCaseDialog}
          setOpen={setOpenEditCaseDialog}
          projectId={caseId}
        />
      )}
      {isDevStage && openSetAsDefaultDialog && (
        <SetAsDefaultCaseDialog
          open={openSetAsDefaultDialog}
          setOpen={setOpenSetAsDefaultDialog}
          projectId={caseId}
        />
      )}
    </>
  );
};
